import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GridSync } from '@grid/models';
import { StaticMessage } from '@shared/enums';
import {
  DprTableOfColumnDetail,
  DprTableOfColumnDetailsItem,
} from '@shared/models/dpr';
import {
  TableOfColumnItemWithValues,
  TableOfColumnWithData,
} from '@shared/models/project-setup';
import { getStaticMessage } from '@shared/utils';
import {
  DprTableOfColumnsAccumulation,
  DprTableOfColumnsFormulaContext,
} from '@dpr-details/models';
import {
  applyCarryOverValues,
  getRowAnchorValue,
  hasNewDivisionByZeroError,
  isTableOfColumnItemDerived,
  normalizeAnchorValue,
  openValidationErrorDialog,
} from '@dpr-details/utils';
import { DprFormulaCalculationService } from '../formula';

@Injectable()
export class DprTableOfColumnsGridService {
  constructor(
    private dialog: MatDialog,
    private dprFormulaCalculationService: DprFormulaCalculationService
  ) {}

  buildGridData(
    dprTableOfColumnsDetails: DprTableOfColumnDetail[],
    accumulation?: DprTableOfColumnsAccumulation,
    formulaContext?: DprTableOfColumnsFormulaContext
  ): DprTableOfColumnDetail[] {
    const result = [];

    dprTableOfColumnsDetails?.forEach((detail) => {
      const dprTableOfColumnDetail = {
        id: detail.id,
        dprTableOfColumnId: detail.dprTableOfColumnId,
      };

      // pivot tables of columns detail items to columns. Whatever the carry-over
      // and formula items arrive with is replaced below: carry-overs first, then
      // the formulas, which can read them.
      detail.dprTableOfColumnDetailItems.forEach((item) => {
        dprTableOfColumnDetail[item.tableOfColumnItemId] = item.value ?? null;
      });

      if (accumulation) {
        applyCarryOverValues(
          accumulation,
          getRowAnchorValue(detail, accumulation.anchorColumnId),
          (itemId, value) => {
            dprTableOfColumnDetail[itemId] = value;
          }
        );
      }

      result.push(dprTableOfColumnDetail);
    });

    if (formulaContext) {
      this.dprFormulaCalculationService.applyTableOfColumnsFormulas(
        formulaContext,
        result
      );
    }

    return result;
  }

  /**
   * Rows are matched across DPRs through their anchor option, so a table with an
   * anchor column only saves rows that name one.
   */
  validateChanges(
    changes: GridSync<DprTableOfColumnDetail>,
    tableOfColumn: TableOfColumnWithData
  ): boolean {
    const anchorColumnId = tableOfColumn?.anchorColumn?.anchorColumnId;
    const anchorItem =
      anchorColumnId == null
        ? undefined
        : tableOfColumn.tableOfColumnItems?.find(
            (item) => +item.id === +anchorColumnId
          );

    if (!anchorItem) {
      return true;
    }

    const entities = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ];

    for (const entity of entities) {
      const errorMessages = [];

      if (normalizeAnchorValue(entity[anchorItem.id]) === null) {
        errorMessages.push(
          getStaticMessage(StaticMessage.FieldMandatoryError, anchorItem.name)
        );
      }

      if (errorMessages.length) {
        openValidationErrorDialog(
          this.dialog,
          tableOfColumn.name,
          errorMessages.join('<br>')
        );

        return false;
      }
    }

    return true;
  }

  /**
   * Each edited row is compared with its stored row and with itself holding only its
   * anchor option, as a copied row does. An error either already shows comes from
   * an earlier save or from the DPR before, so it does not block the save.
   */
  validateFormulaChanges(
    changes: GridSync<DprTableOfColumnDetail>,
    storedRows: DprTableOfColumnDetail[],
    formulaContext: DprTableOfColumnsFormulaContext | undefined,
    accumulation: DprTableOfColumnsAccumulation | undefined,
    tableOfColumn: TableOfColumnWithData
  ): boolean {
    if (!formulaContext) {
      return true;
    }

    const editedRows = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ];

    if (
      !hasNewDivisionByZeroError(
        this.dprFormulaCalculationService.calculateTableOfColumnsRows(
          formulaContext,
          editedRows
        ),
        this.dprFormulaCalculationService.calculateTableOfColumnsRows(
          formulaContext,
          storedRows
        ),
        this.dprFormulaCalculationService.calculateTableOfColumnsRows(
          formulaContext,
          editedRows.map((row) =>
            this.buildAnchorOnlyRow(row, formulaContext, accumulation)
          )
        )
      )
    ) {
      return true;
    }

    openValidationErrorDialog(
      this.dialog,
      tableOfColumn?.name,
      getStaticMessage(StaticMessage.DivisionByZero)
    );

    return false;
  }

  mapToGridSync(
    dprTableOfColumnId: number | string,
    changes: GridSync<DprTableOfColumnDetail>,
    dprTableOfColumnDetails: DprTableOfColumnDetail[],
    tableOfColumItemsList: TableOfColumnItemWithValues[]
  ): GridSync<DprTableOfColumnDetail> {
    const result = {
      addedList: [],
      updatedList: [],
      deletedIds: changes?.deletedIds ?? [],
    };
    result.addedList =
      changes?.addedList?.map((data) => {
        const dprTableOfColumnDetailItems = this.buildDetailsItems(
          data,
          tableOfColumItemsList
        );

        const dprTableOfColumnDetail: DprTableOfColumnDetail = {
          id: data.id,
          dprTableOfColumnId,
          dprTableOfColumnDetailItems,
        };

        return dprTableOfColumnDetail;
      }) ?? [];

    result.updatedList =
      changes?.updatedList?.map((x) => {
        const existingDprTableOfColumnDetail = dprTableOfColumnDetails.find(
          (y) => y.id === x.id
        );

        const dprTableOfColumnDetailItems = this.buildDetailsItems(
          x,
          tableOfColumItemsList,
          existingDprTableOfColumnDetail
        );

        const dprTableOfColumnDetail: DprTableOfColumnDetail = {
          id: x.id,
          dprTableOfColumnId,
          dprTableOfColumnDetailItems,
        };

        return dprTableOfColumnDetail;
      }) ?? [];

    return result;
  }

  private buildDetailsItems(
    row: DprTableOfColumnDetail,
    tableOfColumnItemList: TableOfColumnItemWithValues[],
    existingDprTableOfColumnDetail?: DprTableOfColumnDetail
  ): DprTableOfColumnDetailsItem[] {
    const dprTableOfColumnDetailsItems: DprTableOfColumnDetailsItem[] = [];

    for (const tableOfColumnItem of tableOfColumnItemList) {
      // A carry-over or formula value is worked out again every time the DPR
      // is shown, so a copy saved with the row would only go stale.
      if (isTableOfColumnItemDerived(tableOfColumnItem)) {
        continue;
      }

      const existingDprTableOfColumnDetailItem =
        existingDprTableOfColumnDetail?.dprTableOfColumnDetailItems?.find(
          (x) => x.tableOfColumnItemId === tableOfColumnItem.id
        );

      dprTableOfColumnDetailsItems.push({
        ...existingDprTableOfColumnDetailItem,
        tableOfColumnItemId: tableOfColumnItem.id,
        value: row[tableOfColumnItem.id] ?? null,
      });
    }

    return dprTableOfColumnDetailsItems;
  }

  /** The row with only its anchor option and the values it carries over. */
  private buildAnchorOnlyRow(
    row: DprTableOfColumnDetail,
    formulaContext: DprTableOfColumnsFormulaContext,
    accumulation: DprTableOfColumnsAccumulation | undefined
  ): DprTableOfColumnDetail {
    const anchorOnlyRow = {
      id: row.id,
      dprTableOfColumnId: row.dprTableOfColumnId,
    } as DprTableOfColumnDetail;

    if (formulaContext.anchorColumnId != null) {
      anchorOnlyRow[formulaContext.anchorColumnId] =
        row[formulaContext.anchorColumnId] ?? null;
    }

    if (accumulation) {
      applyCarryOverValues(
        accumulation,
        normalizeAnchorValue(row[accumulation.anchorColumnId]),
        (itemId, value) => {
          anchorOnlyRow[itemId] = value;
        }
      );
    }

    return anchorOnlyRow;
  }
}
